import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import './LightningRadar.scss';

// Compass assets for the radar background (includes range rings)
import compassRose2Dark from '../../__assets/img/dashboard/dark/compassrose-2.svg';
import compassRose2Light from '../../__assets/img/dashboard/light/compassrose-2.svg';

// Color based on time since strike
const getStrikeColor = (minutesAgo) => {
	if (minutesAgo <= 5) return '#e74c3c'; // Red
	if (minutesAgo <= 10) return '#f1c40f'; // Yellow
	return '#27ae60'; // Green
};

// Calculate position on radar based on distance (no direction data)
// Map distance to the correct ring zone (rings at 5, 10, 25, 50 NM)
const calculateStrikePosition = (distance, index) => {
	// Visual radius mapping: 0-25% = 0-5NM, 25-50% = 5-10NM, 50-75% = 10-25NM, 75-100% = 25-50NM
	let normalizedDist;
	if (distance <= 5) {
		normalizedDist = (distance / 5) * 0.25;                    // 0-5 NM → 0-25%
	} else if (distance <= 10) {
		normalizedDist = 0.25 + ((distance - 5) / 5) * 0.25;       // 5-10 NM → 25-50%
	} else if (distance <= 25) {
		normalizedDist = 0.50 + ((distance - 10) / 15) * 0.25;     // 10-25 NM → 50-75%
	} else {
		normalizedDist = 0.75 + ((distance - 25) / 25) * 0.25;     // 25-50 NM → 75-100%
	}
	normalizedDist = Math.min(normalizedDist, 1);

	// Golden angle distribution for even spread
	const goldenAngle = 137.508 * (Math.PI / 180);
	const angle = index * goldenAngle;

	// Calculate x, y position (centered at 50,50)
	const x = Math.cos(angle) * normalizedDist * 45; // 45% of container
	const y = Math.sin(angle) * normalizedDist * 45;

	return { x: 50 + x, y: 50 + y };
};

const LightningRadar = ({
	theme = 'dark',
	size = 180,
	// Lightning data for different time periods
	strikeCount1min = 0,
	strikeDist1min = null,
	strikeCount10min = 0,
	strikeDist10min = null,
	strikeCount60min = 0,
	strikeDist60min = null,
}) => {
	const compassRose = theme === 'light' ? compassRose2Light : compassRose2Dark;

	// Generate strike indicators based on available data
	const strikes = useMemo(() => {
		const result = [];
		let index = 0;

		// Add 1-minute strikes (red)
		if (strikeCount1min > 0 && strikeDist1min !== null) {
			for (let i = 0; i < strikeCount1min; i++) {
				const pos = calculateStrikePosition(strikeDist1min, index++);
				result.push({
					id: `1min-${i}`,
					...pos,
					color: getStrikeColor(1),
					size: 8,
				});
			}
		}

		// Add 10-minute strikes (yellow)
		if (strikeCount10min > 0 && strikeDist10min !== null) {
			// Don't duplicate - show strikes beyond 1min
			const additionalStrikes = Math.max(0, strikeCount10min - strikeCount1min);
			for (let i = 0; i < additionalStrikes; i++) {
				const pos = calculateStrikePosition(strikeDist10min, index++);
				result.push({
					id: `10min-${i}`,
					...pos,
					color: getStrikeColor(7), // 7 mins = yellow
					size: 6,
				});
			}
		}

		// Add 60-minute strikes (green)
		if (strikeCount60min > 0 && strikeDist60min !== null) {
			// Don't duplicate - show strikes beyond 10min
			const additionalStrikes = Math.max(0, strikeCount60min - strikeCount10min);
			for (let i = 0; i < additionalStrikes; i++) {
				const pos = calculateStrikePosition(strikeDist60min, index++);
				result.push({
					id: `60min-${i}`,
					...pos,
					color: getStrikeColor(15), // 15 mins = green
					size: 5,
				});
			}
		}

		return result;
	}, [strikeCount1min, strikeDist1min, strikeCount10min, strikeDist10min, strikeCount60min, strikeDist60min]);

	return (
		<div className="lightning-radar" style={{ width: size, height: size }}>
			{/* Compass/Radar Background */}
			<img
				src={compassRose}
				alt="Radar Background"
				className="radar-background"
			/>

			{/* Strike indicators overlay - range rings are part of the compass rose SVG */}
			<svg className="strike-overlay" viewBox="0 0 100 100">
				{strikes.map((strike) => (
					<circle
						key={strike.id}
						cx={strike.x}
						cy={strike.y}
						r={strike.size / 2}
						fill={strike.color}
						className="strike-indicator"
					/>
				))}
			</svg>
		</div>
	);
};

LightningRadar.propTypes = {
	theme: PropTypes.oneOf(['dark', 'light']),
	size: PropTypes.number,
	strikeCount1min: PropTypes.number,
	strikeDist1min: PropTypes.number,
	strikeCount10min: PropTypes.number,
	strikeDist10min: PropTypes.number,
	strikeCount60min: PropTypes.number,
	strikeDist60min: PropTypes.number,
};

// Legend component for external use
export const LightningLegend = () => (
	<div className="lightning-legend">
		<div className="legend-item">
			<span className="legend-dot red"></span>
			<span>≤ 5 min</span>
		</div>
		<div className="legend-item">
			<span className="legend-dot yellow"></span>
			<span>&gt; 5 to ≤ 10 min</span>
		</div>
		<div className="legend-item">
			<span className="legend-dot green"></span>
			<span>&gt; 10 to ≤ 15 min</span>
		</div>
	</div>
);

export default LightningRadar;
